import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Button, Select, Space, Tag, message, Modal, Form, Input } from 'antd';
import { PlusOutlined, ReloadOutlined, HistoryOutlined, PlayCircleOutlined, EditOutlined } from '@ant-design/icons';
import { CompanyArtifactId, CArtifact } from '../../types/artifacts-branded';
import { ArtifactItem, ArtifactItemState } from '../../types/artifactItems';
import { ArtifactService } from '../../services/api/artifactService';
import { StateService } from '../../services/api/stateService';
import { ArtifactItemService } from '../../services/api/artifactItemService';
import { AddArtifactItemDialog } from './AddArtifactItemDialog';
import { AvailableTransitions } from './AvailableTransitions';
import { TransitionExecutor } from './TransitionExecutor';
import { StateHistoryViewer } from './StateHistoryViewer';
import { useAutomaticTransitions } from '../../hooks/useAutomaticTransitions';
import styles from './ArtifactItemsTab.module.css';

const { Option } = Select;

interface ArtifactItemsTabProps {
  artifactId: CompanyArtifactId;
  artifact: CArtifact | null;
}

type SortOrder = 'ascend' | 'descend' | null;
type SortField = 'name' | 'state' | 'created_at' | null;

export const ArtifactItemsTab: React.FC<ArtifactItemsTabProps> = ({
  artifactId,
  artifact: _artifact,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ArtifactItem[]>([]);
  const [states, setStates] = useState<ArtifactItemState[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedStateFilter, setSelectedStateFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [transitionExecutorVisible, setTransitionExecutorVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ArtifactItem | null>(null);
  const [historyViewItemId, setHistoryViewItemId] = useState<string | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ArtifactItem | null>(null);
  const [editForm] = Form.useForm();

  // Enable automatic transitions polling
  useAutomaticTransitions({
    artifactId,
    items,
    pollInterval: 5 * 60 * 1000, // 5 minutes
    onTransitionExecuted: () => {
      // Reload data when automatic transition executes
      void loadData();
    },
    enabled: true,
  });

  useEffect(() => {
    if (artifactId) {
      void loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId]);

  const loadData = async () => {
    if (!artifactId) return;

    try {
      setLoading(true);
      
      // Load items and states in parallel
      // Use new ArtifactItemService for better filtering and state management
      const [itemsData, statesData] = await Promise.all([
        ArtifactItemService.getItemsByArtifact(artifactId).catch(() => 
          // Fallback to ArtifactService if new service fails
          ArtifactService.getArtifactItems(artifactId)
        ),
        StateService.getStatesByArtifact(artifactId),
      ]);

      setItems(itemsData || []);
      setStates(statesData || []);
    } catch (error) {
      console.error('Error loading artifact items:', error);
      message.error('Failed to load artifact items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuccess = () => {
    setShowAddDialog(false);
    void loadData();
  };

  // Deprecated: Direct state change - kept for backward compatibility
  // New code should use transitions via TransitionExecutor
  const _handleStateChange = async (itemId: string, newStateId: string) => {
    try {
      await ArtifactService.updateArtifactItemState(itemId, newStateId);
      message.success('Item state updated successfully');
      void loadData();
    } catch (error: any) {
      console.error('Error updating item state:', error);
      message.error(error.response?.data?.detail || error.message || 'Failed to update item state');
    }
  };

  const handleTransitionExecute = (item: ArtifactItem) => {
    setSelectedItem(item);
    setTransitionExecutorVisible(true);
  };

  const handleTransitionComplete = () => {
    setTransitionExecutorVisible(false);
    setSelectedItem(null);
    void loadData();
  };

  const handleViewHistory = (itemId: string) => {
    if (historyViewItemId === itemId) {
      setHistoryViewItemId(null);
    } else {
      setHistoryViewItemId(itemId);
    }
  };

  const handleEditItem = (item: ArtifactItem) => {
    setEditingItem(item);
    editForm.setFieldsValue({
      name: item.name,
      description: item.description || ''
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    if (!editingItem) return;

    try {
      const values = await editForm.validateFields();
      await ArtifactItemService.updateItem(editingItem.id, {
        name: values.name,
        description: values.description
      });
      message.success('Item updated successfully');
      setEditModalVisible(false);
      setEditingItem(null);
      editForm.resetFields();
      void loadData();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      console.error('Error updating item:', error);
      message.error(error.response?.data?.detail || error.message || 'Failed to update item');
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingItem(null);
    editForm.resetFields();
  };

  // Get state name by ID
  const getStateName = useCallback((stateId: string | undefined): string => {
    if (!stateId) return 'Unknown';
    const state = states.find((s) => s.id === stateId);
    return state?.name || 'Unknown';
  }, [states]);

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...items];

    // Filter by state
    if (selectedStateFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const currentStateId = item.current_state_id || item.state_id || item.state;
        return currentStateId === selectedStateFilter;
      });
    }

    // Sort
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'state':
            aValue = getStateName(a.current_state_id || a.state_id || a.state);
            bValue = getStateName(b.current_state_id || b.state_id || b.state);
            break;
          case 'created_at':
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'ascend' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'ascend' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [items, selectedStateFilter, sortField, sortOrder, getStateName]);

  // Get unique states from items for filter dropdown
  const availableStates = useMemo(() => {
    const stateIds = new Set<string>();
    items.forEach((item) => {
      const stateId = item.current_state_id || item.state_id || item.state;
      if (stateId) {
        stateIds.add(stateId);
      }
    });
    return Array.from(stateIds).map((id) => states.find((s) => s.id === id)).filter(Boolean) as ArtifactItemState[];
  }, [items, states]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: string, record: ArtifactItem) => text || `Item ${record.id.substring(0, 8)}`,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: 'State',
      key: 'state',
      sorter: true,
      render: (_: any, record: ArtifactItem) => {
        const currentStateId = record.current_state_id || record.state_id || record.state;
        const state = states.find((s) => s.id === currentStateId);
        return (
          <Tag color="blue">{state?.name || 'Unknown'}</Tag>
        );
      },
    },
    {
      title: 'Transitions',
      key: 'transitions',
      render: (_: any, record: ArtifactItem) => (
        <AvailableTransitions
          artifactId={artifactId}
          item={record}
          onTransitionExecute={() => handleTransitionExecute(record)}
          compact
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ArtifactItem) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditItem(record)}
            size="small"
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<PlayCircleOutlined />}
            onClick={() => handleTransitionExecute(record)}
            size="small"
          >
            Execute Transition
          </Button>
          <Button
            type="link"
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record.id)}
            size="small"
          >
            History
          </Button>
        </Space>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => quantity ?? '-',
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      render: (unit: string) => unit || '-',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '-'),
    },
  ];

  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter.field) {
      setSortField(sorter.field as SortField);
      setSortOrder(sorter.order as SortOrder);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  return (
    <div className={styles.artifactItemsTab}>
      <div className={styles.artifactItemsHeader}>
        <h3>Artifact Items</h3>
        <Space>
          <Select
            value={selectedStateFilter}
            onChange={setSelectedStateFilter}
            style={{ width: 200 }}
            placeholder="Filter by state"
          >
            <Option value="all">All States</Option>
            {availableStates.map((state) => (
              <Option key={state.id} value={state.id}>
                {state.name}
              </Option>
            ))}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddDialog(true)}
          >
            Add Item
          </Button>
        </Space>
      </div>
      <div className={styles.artifactItemsContent}>
        <Table
          dataSource={filteredAndSortedItems}
          columns={columns}
          rowKey="id"
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          locale={{
            emptyText: 'No artifact items found. Click "Add Item" to create one.',
          }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as React.Key[]),
            expandedRowRender: (record) => {
              if (historyViewItemId === record.id) {
                return (
                  <div style={{ padding: 16 }}>
                    <StateHistoryViewer
                      itemId={record.id}
                      artifactId={artifactId}
                      onRollbackComplete={loadData}
                    />
                  </div>
                );
              }
              return null;
            },
          }}
        />
      </div>

      {selectedItem && (
        <TransitionExecutor
          artifactId={artifactId}
          item={selectedItem}
          visible={transitionExecutorVisible}
          onCancel={() => {
            setTransitionExecutorVisible(false);
            setSelectedItem(null);
          }}
          onTransitionComplete={handleTransitionComplete}
        />
      )}

      <AddArtifactItemDialog
        visible={showAddDialog}
        artifactId={artifactId}
        onCancel={() => setShowAddDialog(false)}
        onSuccess={handleAddSuccess}
      />

      <Modal
        title="Edit Artifact Item"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={handleEditCancel}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Item name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Item description" 
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

